import clsx from "clsx";

import { Code as MantineCode } from "@mantine/core";

export const Code = ({
  children,
  className,
  textColor = "text-venus+0",
  block = false,
}: {
  children: React.ReactNode;
  className?: string;
  textColor?: string;
  block?: boolean;
}) => {
  return block ? (
    <div
      className={clsx(
        textColor,
        "p-4 bg-interstellar-3 rounded border border-gray inline-flex",
        className,
      )}
    >
      <div className="break-all grow shrink basis-0">{children}</div>
    </div>
  ) : (
    <MantineCode className={clsx(textColor, "rounded border px-2", className)}>
      {children}
    </MantineCode>
  );
};

export const SubSectionHeader = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <h1
    className={clsx(
      "heading mx-0 mb-0 mt-0 section-counter-item text-lg font-semibold leading-normal",
      className,
    )}
  >
    {children}
  </h1>
);

export const SubSubSectionHeader = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <h2
    className={clsx(
      "heading mx-0 mb-0 mt-0 text-sm font-semibold leading-tight",
      className,
    )}
  >
    {children}
  </h2>
);

export const Divider = () => <hr className="my-6 bg-gray" />;

/**
 * The value used to indent sub lists in OrderedList.
 * @param multiplier
 * @returns A value that can be used as a className to indent an element.
 */
export function ListIndent(multiplier?: number) {
  return `ml-[${28 * (multiplier ?? 1)}px]`;
}

export const OrderedList = ({
  variant: listStyleType = "decimal",
  children,
  className,
  indent = 0,
}: {
  variant?: "decimal" | "upper-alpha" | "disc";
  children: React.ReactNode | React.ReactNode[];
  className?: string;
  indent?: number;
}) => {
  const nodes = Array.isArray(children) ? children : Array.of(children);
  return (
    <ol
      style={{ listStyleType }}
      className={clsx(
        "list-inside m-0 space-y-2",
        {
          [ListIndent(indent)]: indent,
          "mt-2": indent > 0,
          "marker:text-xxs": listStyleType === "disc",
        },
        className,
      )}
    >
      {nodes.map((child, index) => (
        <li key={index}>
          <i className="ml-2" />
          {child}
        </li>
      ))}
    </ol>
  );
};
